import type { ComponentProps } from "svelte";
import { cartesianProduct } from "../utils/cartesian";
import type FilterSelect from "../ui-components/filter-select.svelte";
import type { SelectorSelections, SeriesOptions, Options, Query } from "./types";
import { t, locale } from "../translations/i18n";
import { get } from 'svelte/store';
import { visualizationDataStore } from "./stores";
import { v4 as uuidv4 } from 'uuid';
import { tick } from 'svelte';

let currentLanguage: string = get(locale).split("-")[0]


/**
 * Converts selector UI selections into series options for data queries
 *
 * @param selectorSelections Selections made on the selector
 * @returns All combinations of the selections as SeriesOptions
 */
export const selectorSelectionsToSeriesOptions = (
    selectorSelections: SelectorSelections
  ): SeriesOptions[] => {
    const selectorSelectionsArray = Object.keys(
      selectorSelections.selections
    ).map((key) => {
      return selectorSelections.selections[key]["values_db"];
    });

    const selectorSelectionsVerboseArray = Object.keys(
      selectorSelections.selections
    ).map((key) => {
      return selectorSelections.selections[key]["values"];
    });
    const cartesian = cartesianProduct(...selectorSelectionsArray);
    const cartesian_verbose = cartesianProduct(...selectorSelectionsVerboseArray);

    const seriesOptions = cartesian.map((c: any[], index: number) => {
      const obj: SeriesOptions = {
        statName: selectorSelections.statName,
        statVerboseName: selectorSelections.statVerboseName,
        is_daily: selectorSelections.is_daily,
        options: {},
        verbose_options: {},
      };
      Object.keys(selectorSelections.selections).forEach(
        (key: string, i: number) => {
          obj.options[key] = c[i];
        }
      );
      // Fill verbose_options
      const verboseValues = cartesian_verbose[index]; // Get corresponding verbose values
      Object.keys(selectorSelections.selections).forEach(
        (key: string, i: number) => {
          obj.verbose_options[selectorSelections.selections[key].verbose_key_name[currentLanguage]] = verboseValues[i];
        }
      );
      return obj;
    });

    return seriesOptions;
  };


/**
 * Sort object by own keys or external array of keys
 *
 * @param sortable The object to sort
 * @param sortBy Optional array of keys to sort by. If not provided,
 *               the keys of the object will be used.
 * @returns the sorted object
 */
export const sortObject = (
    sortable: { [key: string]: any },
    sortBy: string[] = []
  ): { [key: string]: any } => {
    if (sortBy.length === 0) {
      sortBy = Object.keys(sortable);
    }
    return Object.keys(sortable)
      .sort((a, b) => sortBy.indexOf(a) - sortBy.indexOf(b))
      .reduce((sorted, key) => {
        sorted[key] = sortable[key];
        return sorted;
      }, {} as { [key: string]: any });
  };


/**
 * Helper function to get options key
 *
 * @param value - One of the options
 * @returns name of the key as string
 */
export const keyOf = (value: any, options: any): string => {
  const keys = Object.keys(options) as (keyof Options)[];
  const key = keys.find((k) => options[k] === value);
  if (key === undefined) {
    throw new Error("Incorrect key");
  }
  return key;
};


/**
 * Generates a random UUID (Universally Unique Identifier) using the v4 method from the 'uuid' library.
 * The v4 method generates a UUID based on random numbers, ensuring a high probability of uniqueness.
 * 
 * The UUID is then concatenated with the current timestamp for store sorting purposes.
 * 
 * @returns {string} A randomly generated UUID string.
 */
export const generateUID = (): string => {
  return uuidv4().concat("-").concat(new Date().getTime().toString());
};


/**
 * Create Query from options and series options.
 * The subset of the options that is exposed to the data source is selected here.
 *
 * @param options - Options to create query from
 * @returns Query object built from options and series options
 */
export const createQuery = (
  options: Options,
  seriesOptions: SeriesOptions
): Query => ({
  dateFilter: options.dateFilter,
  dataValueType: options.dataValueType,
  seriesOptions,
});


/**
 * Sorts the options in the filter select so that disabled options are at the bottom
 * and selected options are at the top.
 *
 * @param list - The list of filter select props
 */
export const sortByDisabledAndSelected = (list: any[]): void => {
  list.forEach((field) => {
    field.options.sort((a: any, b: any) => {
      if (a.disabled === b.disabled && a.selected === b.selected) return 0;
      if (a.disabled) return 1;
      if (b.disabled) return -1;
      if (a.selected) return -1;
      return 1;
    });
  });
};


/**
   * Sorts the options in the filter alphabetically while preserving selected options' order
   * 
   * @param list - The list of filter select props
   */
export const sortOptionsAlphabetically = (list: any, initialSorting = false): any => {
  if (initialSorting) {
    list = [{"options": list}]
  }
  list.forEach((field: any) => {
    // Make a deep copy of field.options
    const optionsCopy = JSON.parse(JSON.stringify(field.options));

    optionsCopy.forEach((option: any) => {
      option.selected = option.selected || false; // Add "selected: false" to each option if not already present
    });

    // Sort all options alphabetically
    optionsCopy.sort((a: any, b: any) => a.name[currentLanguage].localeCompare(b.name[currentLanguage]));

    // If name["en"] is "Total", move it as a first option of optionsCopy
    const totalOption = optionsCopy.find((option: any) => option.name["en"] === "Total");
    if (totalOption) {
      optionsCopy.splice(optionsCopy.indexOf(totalOption), 1);
      optionsCopy.unshift(totalOption);
    }

    // Separate selected and unselected options
    const selectedOptions = optionsCopy.filter((option: any) => option.selected);
    const unselectedOptions = optionsCopy.filter((option: any) => !option.selected);

    // Combine selected and unselected options while preserving selected options' order
    field.options = selectedOptions.map((selectedOption: any) => ({
      ...selectedOption,
      selected: true,
    })).concat(unselectedOptions);
  });

  if (initialSorting) {
    return list[0].options
  }

  return list;
};


/**
 * Function for combining the updated fields with the original fields.
 * Works so that disabled fields are updated in the original object.
 */
export const updateProps = async (updatedFields: any, filterSelectProps: ComponentProps<FilterSelect>[]): Promise<void>  => {
  // Update the "disabled" property in the original array
  return filterSelectProps.forEach(fieldItem => {
    const updatedItem = updatedFields.find((item: any) => item.name[currentLanguage] === fieldItem.title);
    if (updatedItem) {
      fieldItem.options.forEach(option => {
        const sourceOption = updatedItem.options.find((opt: any) => opt.name[currentLanguage] === option.name[currentLanguage]);

        if (sourceOption) {
          option.disabled = sourceOption.disabled;
        }
      });
    }
  });
};


/**
   * Translate a string
   *
   * @param input - String to translate
   */
export const translate = (input: string): string => {
  let translatedText = '';       

  t.subscribe((translationFunction) => {
    translatedText = translationFunction(input);
  });
  return translatedText;
}


/**
 * Get the view data collection from local storage
 * 
 * @returns json object of the view data collection
 */
export const getViewData = () => {
  let viewData: any[] = [];

  visualizationDataStore.subscribe((data) => {
    viewData = data;
  });

  return viewData;
};


/**
   * This function checks if a date is included in an array of dates.
   * It checks only the date (year, month, day) excluding the time and timezone.
   * @param dates_array - array of dates
   * @param date_to_check - date to check if it is included in the dates_array
   * @param returnIndex - if true, the function returns the index of the date in the array
   * @returns boolean or number (if returnIndex is true)
   */
export function checkDateIncludedInArray(dates_array: Array<Date>, date_to_check: Date, returnIndex: boolean = false) {
  // Extract year, month, and day from the date to check
  const yearToCheck = date_to_check.getFullYear();
  const monthToCheck = date_to_check.getMonth();
  const dayToCheck = date_to_check.getDate();

  if (returnIndex) {
    // Check if any date in the array matches the date to check
    return dates_array.findIndex((date: Date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      return year === yearToCheck && month === monthToCheck && day === dayToCheck;
    });
  } else {
    // Check if any date in the array matches the date to check
    return dates_array.some((date: Date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      return year === yearToCheck && month === monthToCheck && day === dayToCheck;
    });
  }
}

/**
 * Function for hiding the header of visualization.
 * @param uniqueId - Unique identifier of the visualization
 */
export function hideContainerHeader(uniqueId: string) {
  const containerHeader = document.getElementById(`header-id-${uniqueId}`);
  if (containerHeader) {
    containerHeader.style.display = "none";
  }
  const mainElement = document.getElementById(`main-id-${uniqueId}`) as HTMLElement;
  if (mainElement) {
    mainElement.style.height = 'var(--visualization-height)';
    mainElement.style.paddingTop = '1rem';
  }
  const containerElements = document.getElementById(`container-id-${uniqueId}`) as HTMLElement;
  if (containerElements) {
    containerElements.style.height = 'var(--visualization-height)';
  }
}

/**
 * Function for showing the header of visualization.
 * @param uniqueId - Unique identifier of the visualization
 */
export function showContainerHeader(uniqueId: string) {
  // Show the visualization header
  const headerElement = document.getElementById(`header-id-${uniqueId}`) as HTMLElement;
  headerElement.style.display = 'flex';

  // Change the visualization main height
  const mainElement = document.getElementById(`main-id-${uniqueId}`) as HTMLElement;
  mainElement.style.height = 'calc(var(--visualization-height) - var(--header-height))';
  mainElement.style.paddingTop = '0';

  // Change the visualization container height
  const containerElements = document.getElementById(`container-id-${uniqueId}`) as HTMLElement;
  containerElements.style.height = 'var(--visualization-height)';
}

/**
   * Function for handling visualization header visibility
   * @param showHeader - Show or hide the header
   */
export const handleHeader = async (showHeader: boolean, uniqueId: string) => {
  await tick;
  if (showHeader) {
    showContainerHeader(uniqueId);
  } else {
    hideContainerHeader(uniqueId);
  }
}
